import { ACTIVITY_EVENTS } from '@client/enums';

import { getDateObject } from '@common/utils/dates';
import { merge } from '@common/utils/objects';

const events = Object.keys(ACTIVITY_EVENTS).reduce((acc, key) => {
  acc[key] = {
    ...ACTIVITY_EVENTS[key],
    count: 0
  };

  return acc;
}, {});

// Mirrors the backend exclusion in mailapi (models/ad.js): a click/delivery is internal when
// the recipient is on the active domain or one of the configured internal domains. Used to keep
// the campaign activity views and Clicks/Deliveries exports consistent with the performance
// metrics, which already exclude internal events server-side when a campaign's countinternal is off.
export const isInternalRecipient = (recipient, domain, internalDomains = []) => {
  if (!recipient) {
    return false;
  }

  const rdomain = recipient.split('@').pop();

  return (domain && recipient.endsWith('@' + domain)) || internalDomains.includes(rdomain);
};

// Bizcard activities have no originating message, so response.js gives them a
// synthetic msglink (`bizcard:<owner>`) to survive buildMsgLogReports, which drops
// msglink-less rows. The resulting pseudo-msglogs must be excluded from anything
// message-shaped — sent counts, recipients, reach — or they skew the email KPIs.
export const BIZCARD_MSGLINK_PREFIX = 'bizcard:';

export const isBizcardMsglog = (msglog) =>
  typeof msglog?.msglink === 'string' && msglog.msglink.startsWith(BIZCARD_MSGLINK_PREFIX);

export const generateActivityStats = (data) => {
  const stats = data.activity.reduce(
    (acc, item) => {
      if (item.objecttype === 'beaconview') {
        acc.viewed.count++;
      } else if (item.objecttype === 'linkclick') {
        acc.linkclick.count++;
      } else if (item.objecttype === 'websitevisit') {
        acc.websitevisit.count++;
      } else if (item.objecttype === 'adclick') {
        acc.adclick.count++;
      } else if (
        item.verb === 'myplayerlaunched' ||
        item.verb === 'playerlaunched' ||
        item.verb === 'myviewerlaunched' ||
        item.verb === 'viewerlaunched'
      ) {
        acc.viewerlaunched.count++;
      } else if (item.verb === 'downloaded') {
        acc.downloaded.count++;
      }

      return acc;
    },
    merge({}, events)
  );

  return stats;
};

const dedupeActivityData = (activity) => {
  // sort by objecttype and utcts
  activity.sort((a, b) => a.objecttype.localeCompare(b.objecttype) || new Date(a.utcts) - new Date(b.utcts));

  // filter out items with the same type and less than 1 second (or 100ms for "beaconwiew") apart timestamps
  return activity.filter((item, index, arr) => {
    // always keep the first item
    if (index === 0) {
      return true;
    }

    const prevItem = arr[index - 1];

    if (
      item.objecttype !== prevItem.objecttype ||
      item?.sender !== prevItem?.sender ||
      item?.recipient !== prevItem?.recipient
    ) {
      return true; // different types, so keep it
    }

    const timeDiff = new Date(item.utcts).getTime() - new Date(prevItem.utcts).getTime();
    // apply different time constraints based on the objecttype
    const timeConstraint = item.objecttype === 'beaconview' ? 50 : 1000;
    // keep if time difference is greater than or equal to the constraint
    return timeDiff >= timeConstraint;
  });
};

export const MessageLogParser = {
  getDateObject,
  getExportHeaders() {
    return [
      'To',
      'Subject',
      'Viewed',
      'Link Clicked',
      'Website Visted',
      'Ad Clicked',
      'Video Launched',
      'Document Launched',
      'Document Downloaded',
      'Lead Score',
      'Tags',
      'Date Sent',
      'Last Activity Date'
    ];
  },

  getExportArray(msglogs) {
    const ret = [];
    msglogs = this.calculateStats(msglogs);
    /// console.log(msglogs);
    msglogs[1].forEach(function (data) {
      let viewed = 0;
      let clicked = 0;
      let visited = 0;
      let adclicked = 0;
      let videolaunched = 0;
      let documentlaunched = 0;
      let documentdownloaded = 0;

      viewed = data.leadscoreTable['beaconview-viewed'] || 0;
      clicked = data.leadscoreTable['linkclick-clicked'] || 0;
      visited = data.leadscoreTable['websitevisited-visited'] || 0;
      adclicked = data.leadscoreTable['adclick-clicked'] || 0;
      videolaunched = data.leadscoreTable['myvideoactivity-myplayerlaunched'] || 0;
      documentlaunched = data.leadscoreTable['attachmentview-viewerlaunched'] || 0;
      documentdownloaded = data.leadscoreTable['attachmentdownload-downloaded'] || 0;

      /*
        if (data.leadscoreTable['beaconview-viewed']) {
          viewed = true;
        }
        if (data.linkclicked) {
          clicked = true;
        }
        if (data.websitevisited) {
          visited = true;
        }
        if (data.adclicked) {
          adclicked = true;
        }
        if (data.videolaunched) {
          videolaunched = true;
        }
        if (data.documentlaunched) {
          documentlaunched = true;
        }
        if (data.documentdownloaded) {
          documentdownloaded = true;
        } */

      ret.push({
        to: (data?.to || '').replace(/[\n\r]/g, '').trim(),
        subject: (data?.msgsubject || '').replace(/[\n\r]/g, '').trim(),
        viewed,
        clicked,
        visited,
        adclicked,
        videolaunched,
        documentlaunched,
        documentdownloaded,
        leadscore: data.leadscore,
        tags: data.tags,
        datesent: data.msgtime,
        lastactivity: data.lastactivity.utcts
      });
    });

    return ret.sort((a, b) => new Date(b.lastactivity) - new Date(a.lastactivity));
  },

  parseQuery(query) {
    let i, so, s;
    let token, items;
    query = query || '';
    s = query.split(',');
    so = {};

    if (
      query.indexOf('Re:') === 0 ||
      query.indexOf('Fwd:') === 0 ||
      query.indexOf('re:') === 0 ||
      query.indexOf('fwd:') === 0
    ) {
      so.all = query.toLowerCase();
      return so;
    }

    for (i = 0; i < s.length; i++) {
      token = s[i].trim();
      items = token.split(':');
      if (items.length > 1) {
        so[items[0]] = items
          .splice(1, items.length - 1)
          .join()
          .toLowerCase();
      } else {
        so.all = items[0].toLowerCase();
      }
    }
    return so;
  },

  search(query, items, searchConfig) {
    let ret = {};
    let retarray;
    let retstats = {};
    //      retstats.reminders = {set: 0, due:0};
    retstats.muted = { set: 0 };
    window.curdate = new Date();

    let queryObject = this.parseQuery(query);
    let searchMatch = function (haystack, needle) {
      if (!needle) {
        return true;
      }
      if (!haystack) {
        return false;
      }
      return haystack.indexOf(needle) !== -1;
    };
    /// //console.log($scope.queryObject);
    if (query && query.length < 1) {
      retarray = items;
    } else {
      // MAIN FILTER ROUTINE
      // make sure leadscore is a number
      if (isNaN(searchConfig.leadscore)) {
        searchConfig.leadscore = 0;
      }
      // at least one activity Filter is chosen.  Match any item that has that
      retarray = items;
      console.log('########## GOING TO PROCESS activityfilter #########');
      console.log(searchConfig.activityFilter);
      if (searchConfig.activityFilter.length < 6) {
        if (searchConfig.activityFilter.length > 0) {
          retarray = retarray.filter(function (item) {
            let ret = false;
            let key, value;
            //   console.log('item activity types');
            //   console.log(item.activityTypes);
            for (key in searchConfig.activityFilter) {
              value = searchConfig.activityFilter[key];
              if (item.activityTypes[value.id]) {
                //      console.log("setting ret = true");
                ret = true;
              }
            }
            return ret;
          });
        } else {
          // search config is blank, only get
          console.log('########## searchConfig blank. get only sent items with no activity #########');
          retarray = retarray.filter(function (item) {
            let ret = true;
            let key, value;
            console.log('searchConfig activity filtion options');
            console.log(searchConfig.activityFilterOptions);
            console.log('item.activityTypes');
            console.log(item.activityTypes);
            for (key in searchConfig.activityFilterOptions) {
              value = searchConfig.activityFilter[key];
              //     console.log('activity filter value:'+value.id);
              if (item.activityTypes[value.id]) {
                //        console.log("setting ret = false");
                ret = false;
              }
            }
            //   console.log("returning ret = "+ret);
            return ret;
          });
        }
      } // end activity filter check
      console.log('########## END PROCESS activityfilter #########');

      retarray = retarray.filter(function (item) {
        let ret;
        let key; //, value;

        // gather stats
        /*          if(item.reminderdate){
          retstats.reminders.set = retstats.reminders.set + 1;
          if(window.curdate > item.reminderdate){
        // console.log("curdate > reminderdate");
            retstats.reminders.due = retstats.reminders.due + 1;
          }
        } */

        if (item.muted) {
          retstats.muted.set = retstats.muted.set + 1;
        }

        // filter data

        if (searchConfig.showPitches && !item.pitchreport && !item.pitchreport2) {
          return false;
        }

        if (searchConfig.showVideos && !item.videoreport && !item.videoreport2) {
          return false;
        }

        if (searchConfig.showFusion && !item.fusionreport && !item.fusionreport2) {
          return false;
        }

        if (item.leadscore < searchConfig.leadscore) {
          return false;
        }

        if (searchConfig.filterMutes === 'Unmuted' && item.muted) {
          //            console.log("filtering muted item:"+item.msglink);
          return false;
        }
        if (searchConfig.filterMutes === 'Muted') {
          if (item.muted) {
            return true;
          } else {
            return false;
          }
        }

        if (!query) {
          return true;
        }

        ret = false;
        //             ///console.log(value);
        for (key in queryObject) {
          // value = searchConfig.activityFilter[key];
          if (searchMatch(item.searchObject[key], queryObject[key])) {
            //                 ///console.log("matched key:"+key+':value:'+value);
            ret = true;
            return;
          }
          if (key === 'all') {
            if (!queryObject.all) {
              return;
            }
            let tokenkey;
            for (tokenkey in item.searchObject) {
              // let tokenvaluevalue = searchConfig.activityFilter[key];
              if (searchMatch(item.searchObject[tokenkey], queryObject.all)) {
                ret = true;
              }
            }
          }
        }
        return ret;
      });
    }

    retarray = retarray.sort(function (a, b) {
      return b['lastactivitydate'] - a['lastactivitydate'];
    });

    // hide  views that happen when a thread is opened
    let lastitem;
    if (searchConfig.hideThreadViews && retarray && retarray.length) {
      retarray = retarray.filter(function (item) {
        // let ret = true;
        if (!lastitem) {
          lastitem = item;
          console.log('first item');
          return true;
        }
        if (
          lastitem.lastactivity.verb === 'viewed' &&
          lastitem.lastactivity.verb === item.lastactivity.verb &&
          lastitem.lastactivity.recipient === item.lastactivity.recipient &&
          lastitem.lastactivitydate.getTime() - item.lastactivitydate.getTime() < 1000
        ) {
          console.log('supressing item');
          console.log(item);
          lastitem = item;
          return false;
        }

        lastitem = item;
        return true;
      });
    }

    ret.filteredItems = retarray;
    ret.filteredStats = retstats;
    // return retarray;
    return ret;
  },

  getMaxTimestamp(rawData) {
    let maxTimestamp = null;

    console.log('###### debug full rawData. max timestamp:');
    let ts;
    if (rawData) {
      for (let i = 0; i < rawData.length; i++) {
        ts = new Date(rawData[i].utcts);
        if (ts > maxTimestamp) {
          maxTimestamp = ts;
        }
      }
    }
    console.log('###### debug full rawData. returning maxTimestamp:' + maxTimestamp);
    return maxTimestamp;
  },

  filterRawData(rawData, filterConfig, userConfig) {
    let filteredData;
    let maxTimestamp;
    let i = 0;
    let len;
    let newArray = [];
    let userip = userConfig.ipinfo.current;
    let userOtherIps = userConfig.ipinfo.cache;
    /// console.log("filtering raw data. MessageLogParser");
    if (rawData === null) {
      return;
    }
    if (typeof rawData !== 'object') {
      return;
    }

    // massage all data objects if they have v2 specs
    let flen = rawData.length;
    let j = 0;
    for (j = 0; j < flen; j++) {
      rawData[j].utctsobj = getDateObject(rawData[j].utcts);
      rawData[j].msgtime = getDateObject(rawData[j].utcmsgtime);
    }

    filteredData = rawData.sort(function (a, b) {
      return a.utctsobj.getTime() - b.utctsobj.getTime();
    });

    console.log('calculating maxtimestamp...');
    console.log('filteredData.length =' + filteredData.length);
    if (filteredData.length) {
      maxTimestamp = filteredData[filteredData.length - 1].utctsobj;
    }
    console.log('####################   maxtimestamp:' + maxTimestamp);

    console.log('CHECKING USERIP:' + userip);
    console.log('hideMyIp set to:' + filterConfig.hideMyIp);
    if (filterConfig.hideMyIp && filteredData) {
      len = filteredData.length;
      i = 0;

      for (i; i < len; i++) {
        if (!filteredData[i].ip || filteredData[i].ip !== userip) {
          newArray.push(filteredData[i]);
        }
      }

      filteredData = newArray;
    }

    let otherIpsString = userOtherIps.toString();
    if (filterConfig.hideMyOtherIps && otherIpsString) {
      newArray = [];
      len = filteredData.length;
      i = 0;
      for (i; i < len; i++) {
        if (!filteredData[i].ip || otherIpsString.indexOf(filteredData[i].ip) === -1) {
          newArray.push(filteredData[i]);
        }
      }
      filteredData = newArray;
    }
    return [filteredData, maxTimestamp];
  },

  calculateStats(logs) {
    const stats = {
      sent: 0,
      uniquerecipients: {},
      uniqueviews: 0,
      uniqueclicks: 0,
      uniqueadclicks: 0,
      uniquevisits: 0,
      uniquevideolaunches: 0,
      uniquedocumentlaunches: 0,
      uniquedocumentdownloads: 0,
      uniquebizcardviews: 0,
      uniquebizcardscans: 0,
      replies: 0
    };

    logs.forEach((data) => {
      // Bizcard pseudo-msglogs are not messages: they must not count toward sent,
      // replies or uniquerecipients. Count their events and move on.
      if (isBizcardMsglog(data)) {
        data.activity.forEach((act) => {
          if (act.objecttype === 'bizcardvisit') {
            stats.uniquebizcardviews = stats.uniquebizcardviews + 1;
          } else if (act.objecttype === 'bizcardscan') {
            stats.uniquebizcardscans = stats.uniquebizcardscans + 1;
          }
        });

        return;
      }

      const isDiscarded = data.activity.some((act) => act.discarded);
      const isReplied = data.activity.some((act) => act.replied);

      if (!isDiscarded) {
        stats.sent = stats.sent + 1;
      }

      if (isReplied) {
        stats.replies = stats.replies + 1;
      }

      stats.uniquerecipients[data.to] = 1;

      data.activity.forEach(function (act) {
        if (act.objecttype === 'beaconview') {
          stats.uniqueviews = stats.uniqueviews + 1;
          data.emailviewed = true;
        } else if (act.objecttype === 'linkclick') {
          data.linkclicked = true;
          stats.uniqueclicks = stats.uniqueclicks + 1;
        } else if (act.objecttype === 'websitevisit') {
          data.websitevisited = true;
          stats.uniquevisits = stats.uniquevisits + 1;
        } else if (act.objecttype === 'adclick') {
          data.adclicked = true;
          stats.uniqueadclicks = stats.uniqueadclicks + 1;
        } else if (act.verb === 'myplayerlaunched' || act.verb === 'playerlaunched') {
          data.videolaunched = true;
          stats.uniquevideolaunches = stats.uniquevideolaunches + 1;
        } else if (act.verb === 'myviewerlaunched' || act.verb === 'viewerlaunched') {
          data.documentlaunched = true;
          stats.uniquedocumentlaunches = stats.uniquedocumentlaunches + 1;
        } else if (act.verb === 'downloaded') {
          stats.uniquedocumentdownloads = stats.uniquedocumentdownloads + 1;
          data.documentdownloaded = true;
        }
      });
    });

    return [stats, logs];
  },

  buildMsgLogReports(filteredData, leadscores) {
    let items = [];
    let isBcc;
    let d1;

    filteredData = dedupeActivityData(filteredData);

    let leadscoreTable = leadscores || {
      'beaconview-viewed': 1,
      'linkclick-clicked': 3,
      'websitevisited-visited': 2,
      'adclick-clicked': 2,
      'attachmentview-viewerlaunched': 4,
      'attachmentdownload-downloaded': 4,
      'myvideoactivity-myplayerlaunched': 4
    };

    let msglogs = {};
    if (typeof filteredData !== 'object') {
      return;
    }
    if (!filteredData) {
      return;
    }
    // sort in chronological order
    //

    filteredData.forEach(function (data) {
      // define blank object

      if (!data || !data.msglink) return;

      if (data && data.msglink && typeof msglogs[data.msglink] === 'undefined') {
        msglogs[data.msglink] = {
          itemtype: 'msglog',
          activity: [],
          dnt: {},
          notrack: {},
          searchObject: {
            from: '',
            origsender: '',
            to: '',
            cc: '',
            bcc: '',
            msgsubject: '',
            msglink: null,
            tags: null,
            video: null,
            filename: null
          },
          recips: {},
          activityTypes: {},
          leadscore: 0,
          leadscoreTable: {
            'beaconview-viewed': 0,
            'linkclick-clicked': 0,
            'websitevisited-visited': 0,
            'adclick-clicked': 2,
            'attachmentview-viewerlaunched': 0,
            'attachmentdownload-downloaded': 0,
            'myvideoactivity-myplayerlaunched': 0
          },
          source: null,
          v2: false,
          // replyFlag: false,
          bcc: '',
          muted: false,
          disablesplitsend: false,
          disableinternal: false,
          lastactivity: null,
          lastactivitydate: null,
          videoreport: null,
          pitchreport: null,
          pitchreport2: null,
          bccsync: null,
          bccsyncaddress: null,
          tags: null,
          origsender: null
          // reminder: null,
          // reminderdate: null
        };
      }

      // console.log('data:');
      // console.log(data);
      // TODO: make sure msglink cannot be blank.
      msglogs[data.msglink].msglink = msglogs[data.msglink].msglink || data.msglink;

      if (data.sender) {
        msglogs[data.msglink].searchObject['from'] =
          msglogs[data.msglink].searchObject['from'] || data.sender.toLowerCase();
      }
      if (data.origsender) {
        msglogs[data.msglink].searchObject['origsender'] =
          msglogs[data.msglink].searchObject['origsender'] || data.origsender.toLowerCase();
      }
      if (data.to) {
        msglogs[data.msglink].to = msglogs[data.msglink].to || data.to;
        msglogs[data.msglink].searchObject['to'] = msglogs[data.msglink].searchObject['to'] || data.to.toLowerCase();
      }
      if (data.cc) {
        msglogs[data.msglink].cc = msglogs[data.msglink].cc || data.cc;
        msglogs[data.msglink].searchObject['cc'] = msglogs[data.msglink].searchObject['cc'] || data.cc.toLowerCase();
      }

      if (data.msgsubject) {
        msglogs[data.msglink].msgsubject = msglogs[data.msglink].msgsubject || data.msgsubject;
        msglogs[data.msglink].searchObject['msgsubject'] =
          msglogs[data.msglink].searchObject['msgsubject'] || data.msgsubject.toLowerCase();
      } else {
        data.msgsubject = '(no subject)';
      }

      // An anonymous activity (a bizcard visit) reaches here with an empty recipient. It is
      // not a person: recording it makes the People panel render a nameless, avatarless card.
      if (data.recipient) {
        msglogs[data.msglink].recips[data.msghashid] = data.recipient;
      }

      msglogs[data.msglink].dnt[data.msghashid] = msglogs[data.msglink].dnt[data.msghashid] || data.donottrack || false;
      msglogs[data.msglink].notrack[data.recipient] =
        msglogs[data.msglink].notrack[data.msghashid] || data.donottrack || false;

      msglogs[data.msglink].searchObject['msglink'] =
        msglogs[data.msglink].searchObject['msglink'] || data.msglink.toLowerCase();
      // console.log('leadscoring:'+data.objecttype+'-'+data.verb);
      // console.log(leadscoreTable[data.objecttype+'-'+data.verb] || 0);

      const objecttype = data.objecttype === 'websitevisit' ? 'websitevisited' : data.objecttype;
      const type = objecttype + '-' + data.verb;

      if (leadscoreTable[type]) {
        msglogs[data.msglink].leadscore += leadscoreTable[type] || 0;
        msglogs[data.msglink].leadscoreTable[type]++;
      }

      msglogs[data.msglink].activityTypes[data.objecttype + '-' + data.verb] = true;

      msglogs[data.msglink].source = data.source || null;

      // add replyflag
      /*        if(data.replyFlag){
        msglogs[data.msglink].replyFlag = data.replyFlag;
      } */

      // add v2 flag for iojs stuff
      if (data.v2) {
        msglogs[data.msglink].v2 = true;
      }

      // add recipient to bcc if not in to or cc

      isBcc = false;

      let recipRegEx = new RegExp(data.recipient.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&'), 'i');

      if (data.to && data.to.search(recipRegEx) === -1 && data.verb === 'sent') {
        if (!data.cc) {
          // ///console.log("no CC. adding BCC:"+data.recipient);
          msglogs[data.msglink].bcc = msglogs[data.msglink]['bcc']
            ? msglogs[data.msglink]['bcc'] + ', ' + data.recipient
            : data.recipient;
          msglogs[data.msglink].searchObject['bcc'] = msglogs[data.msglink].searchObject['bcc']
            ? msglogs[data.msglink].searchObject['bcc'].toLowerCase() + ', ' + data.recipient.toLowerCase()
            : data.recipient.toLowerCase();
          isBcc = true;
        } else if (data.cc && data.cc.search(recipRegEx) === -1) {
          //  ///console.log("with CC. adding BCC:"+data.recipient);
          msglogs[data.msglink].bcc = msglogs[data.msglink]['bcc']
            ? msglogs[data.msglink]['bcc'] + ', ' + data.recipient
            : data.recipient;
          msglogs[data.msglink].searchObject.bcc = msglogs[data.msglink].searchObject.bcc
            ? msglogs[data.msglink].searchObject['bcc'].toLowerCase() + ', ' + data.recipient.toLowerCase()
            : data.recipient.toLowerCase();
          isBcc = true;
        }
      } // end sent

      if (data.disablesplitsend && data.verb !== 'sent') {
        // data.recipient = 'Somebody';
        msglogs[data.msglink].disablesplitsend = true;
      }

      if (data.disableinternal) {
        msglogs[data.msglink].disableinternal = true;
      }

      if (!msglogs[data.msglink].msgtime) {
        if (data.v2 && data.utcmsgtime) {
          msglogs[data.msglink].msgtime = new Date(data.utcmsgtime);
        } else {
          msglogs[data.msglink].msgtime = getDateObject(data.utcmsgtime);
        }
        // make sure BCC sent items are prioritized lower on the log card

        if (!msglogs[data.msglink].lastactivity) {
          // msglogs[data.msglink].lastactivitydate = getDateObject(data.utcmsgtime);
          msglogs[data.msglink].lastactivitydate = data.utctsobj;
          msglogs[data.msglink].lastactivity = data;
        }
      } // end define msgtime

      d1 = data.utctsobj;

      if (
        d1 >= msglogs[data.msglink].lastactivitydate &&
        data.verb !== 'embedded' &&
        data.verb !== 'tracked' &&
        !isBcc
      ) {
        msglogs[data.msglink].lastactivitydate = d1;
        msglogs[data.msglink].lastactivity = data;
      }

      msglogs[data.msglink].activity.push(data);

      // add tags to the object for each search later
      if (data.tags && !msglogs[data.msglink].tags) {
        msglogs[data.msglink].tags = data.tags;
        msglogs[data.msglink].searchObject['tags'] = data.tags.toLowerCase();
      }

      /*        if(data.reminder && !msglogs[data.msglink].reminder){
        msglogs[data.msglink].reminder = data.reminder;
        msglogs[data.msglink].reminderdate = new Date(data.reminder);
      //          msglogs[data.msglink].searchObject['reminder'] = data.reminderdate;
      } */

      if (data.muted && !msglogs[data.msglink].muted) {
        msglogs[data.msglink].muted = data.muted;
      }

      // add myvideo type to log so myvideo directive can be called
      if (
        (data.objecttype === 'myvideo' || data.objecttype === 'video') &&
        data.verb === 'embedded' &&
        data.objectid > 0
      ) {
        if (!msglogs[data.msglink].videoreport) {
          msglogs[data.msglink].videoreport = {};
          if (data.note.name) {
            msglogs[data.msglink].searchObject['video'] = msglogs[data.msglink].searchObject['video']
              ? msglogs[data.msglink].searchObject['video'] + data.note.name.toLowerCase()
              : data.note.name.toLowerCase();
            // TODO: add video title to search object
            //            msglogs[data.msglink].searchObject['video'] = msglogs[data.msglink].searchObject['video'] ? msglogs[data.msglink].searchObject['video'] + data.note.title : data.note.title;
          }
        }
        if (typeof msglogs[data.msglink].videoreport[data.objectid] === 'undefined') {
          msglogs[data.msglink].videoreport[data.objectid] = {
            name: data.note.name,
            title: data.note.title,
            quicktracked: data.note.quicktracked,
            url: data.note.url,
            thumbnail: data.note.thumbnail,
            type: data.note.type,
            hash: data.note.hash,
            duration: data.note.duration
          };
        }
      } // end add video

      if (data.objecttype === 'link' && data.verb === 'embedded' && data.note.linktype === 'video') {
        if (!msglogs[data.msglink].videoreport2) {
          msglogs[data.msglink].videoreport2 = {};
          if (data.note.vidname) {
            msglogs[data.msglink].searchObject['video'] = msglogs[data.msglink].searchObject['video']
              ? msglogs[data.msglink].searchObject['video'] + data.note.vidname.toLowerCase()
              : data.note.vidname.toLowerCase();
            // TODO: add video title to search object
            //            msglogs[data.msglink].searchObject['video'] = msglogs[data.msglink].searchObject['video'] ? msglogs[data.msglink].searchObject['video'] + data.note.title : data.note.title;
          }
        }
        if (data.note.vidid && !msglogs[data.msglink].videoreport2[data.note.vidid]) {
          msglogs[data.msglink].videoreport2[data.note.vidid] = {
            name: data.note.vidname,
            title: data.note.title,
            // quicktracked: data.note.quicktracked,
            quicktracked: true,
            url: data.note.url,
            thumbnail: data.note.thumbnail,
            type: data.note.videotype,
            hash: data.note.vidid,
            duration: data.note.duration
          };
        }
      } // end add video

      // add site fusion
      // if((data.objecttype === 'linkclick' || data.objecttype == 'adclick') && data.verb === 'clicked' && data.note.sitefusion ){
      if (data.note.sitefusion) {
        let freport = 'fusionreport';
        if (data.note.sitefusion === 'sg') freport = 'fusionreport2';

        msglogs[data.msglink][freport] = msglogs[data.msglink][freport] || {};
        msglogs[data.msglink][freport].sessions = msglogs[data.msglink][freport].session || {};
        msglogs[data.msglink][freport].sessions[data.msghashid] =
          msglogs[data.msglink][freport].sessions[data.msghashid] || [];
        msglogs[data.msglink][freport].sessions[data.msghashid].push({ provider: data.note.sitefusion });
      }

      if (data.origsender) msglogs[data.msglink].origsender = data.origsender;

      if (data.bccsync) msglogs[data.msglink].bccsync = true;
      if (data.bccsyncaddress) msglogs[data.msglink].bccsyncaddress = data.bccsyncaddress;
      // begin pitch type
      if (data.pitchid) {
        if (!msglogs[data.msglink].pitchreport) {
          msglogs[data.msglink].pitchreport = {};
          msglogs[data.msglink].pitchreport.pitchid = data.pitchid;
        }
      } // set pitchid

      // setup pitch v2
      if (data.haspitch && !msglogs[data.msglink].pitchreport2) {
        msglogs[data.msglink].pitchreport2 = {};
      }

      // add filenames to search object
      if (data.objecttype === 'attachment') {
        if (data.note.filename) {
          msglogs[data.msglink].searchObject['filename'] = msglogs[data.msglink].searchObject['filename']
            ? msglogs[data.msglink].searchObject['filename'] + data.note.filename.toLowerCase()
            : data.note.filename.toLowerCase();
        }
      }
      // end pitch type
    });

    // flatten the grouped hash array
    if (typeof msglogs === 'object') {
      items.push(...Object.values(msglogs));
    }

    // items.forEach((item) => {
    //   item.activity = dedupeActivityData(item.activity);
    // });

    items.forEach((item) => {
      item.lastactivity = item.activity.reduce((oldest, current) => {
        return new Date(oldest.utcts) > new Date(current.utcts) ? oldest : current;
      });
    });

    return items;
  }
};
